import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import {HiOutlineBriefcase, HiOutlineDocumentDuplicate, HiOutlineViewBoards, HiPencil} from "react-icons/hi";
import MentionTextarea from "./ChatInterface/TextArea/MentionTextarea";
import {useMentionFunctionality} from "./useMentionFunctionality";
import AtDropdown from "./AtDropdown";
import {Chat, Message} from "./types";
import {Mention} from "./ChatInterface/types";
import {useSelector} from "react-redux";
import { getEntityTypeDisplay } from "../../utils/entityDisplayUtils";

interface MemoizedUserMessageProps {
  currentChat: Chat;
  message: Message;
  canEdit: boolean;
  scrollToUserMessage: () => void;
  resetExpansion?: number;
  sseEditMessage: (chatId: string | number | null, messageId: number, newText: string) => void;
  respondWithEditedMessage: (
    responseType: 'answer' | 'modify' | 'approve' | 'cancel',
    editText: string,
    opts?: {
      interruptId?: string;
      chatId?: string | number;
      botMessageId?: number | null;
      userMessageId?: number | null;
    }
  ) => void;
  onResetExpansion?: () => void;
}

const MemoizedUserMessage: React.FC<MemoizedUserMessageProps> = ({
  currentChat,
  message,
  canEdit = false,
  scrollToUserMessage,
  resetExpansion = 0,
  sseEditMessage,
  respondWithEditedMessage,
  onResetExpansion
}) => {
  const MESSAGE_COLLAPSE_THRESHOLD = 300;

  const inputTextRef = useRef<HTMLTextAreaElement | null>(null);
  const atButtonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [showAtDropdown, setShowAtDropdown] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    if (resetExpansion > 0) {
      setEditingMessageId(null);
      setInputText('');
      setShowAtDropdown(false);
    }
  }, [resetExpansion]);

  const {
    lastActiveChatId
  } = useSelector((state: any) => state.chatMessages);

  const {
    isLLMSettingsLoading
  } = useSelector((state: any) => state.settings);

  const {
    inputText,
    setInputText,
    suggestions,
    showSuggestions,
    showDefaultOptions,
    defaultCategories,
    mentions,
    handleInputChange: handleMentionInputChange,
    handleKeyDown: handleMentionKeyDown,
    handleRoadmapSelectInternal,
    closeMentionDropdown,
    getTextWithContextTags,
    setMentions,
    handleSearch,
    currentSearchType,
    currentSearchTerm,
    convertContextTagsToMentions,
    loadingSearch,
    loadingMore,
    selectedSuggestionIndex,
    isDropdownFromButton,
    hasMoreAll,
    hasMoreWorkUnits,
    hasMoreStrategyMaps,
    hasMoreTeams,
    hasMoreResources,
    loadMore,
  } = useMentionFunctionality(inputTextRef);

  const isMessageLong = message.text && message.text.length > MESSAGE_COLLAPSE_THRESHOLD;
  const isCurrentlyEditing = !message.isBot && editingMessageId && message.messageId && editingMessageId === message.messageId;

  const handleSaveEdit = useCallback(async (newText: string) => {
    if(isLLMSettingsLoading) return;

    if (editingMessageId && newText && newText.trim()) {
      setEditingMessageId(null);
      setInputText('');

      if (onResetExpansion) {
        onResetExpansion();
      }

      setTimeout(() => {
        scrollToUserMessage();
      }, 100);

      // Locate the edited user message and the bot message immediately before it
      let botMessageBeforeUser: any = null;
      try {
        const msgs = currentChat?.messages || [];
        const userIdx = msgs.findIndex((m: any) => !m.isBot && m.messageId === editingMessageId);
        if (userIdx > 0) {
          for (let i = userIdx - 1; i >= 0; i--) {
            if (msgs[i]?.isBot) { botMessageBeforeUser = msgs[i]; break; }
          }
        }
      } catch {}

      // Fallback to last bot message only if we didn't find a preceding one
      const fallbackLastBot = botMessageBeforeUser || currentChat?.messages?.slice().reverse().find(m => m.isBot) || null;

      const targetBotMsg = botMessageBeforeUser || fallbackLastBot;
      const botMessageId = (targetBotMsg && typeof (targetBotMsg as any).messageId === 'number') ? (targetBotMsg as any).messageId : null;

      const hasQuestionInterrupt = !!(targetBotMsg && Array.isArray((targetBotMsg as any).interrupts) && (targetBotMsg as any).interrupts.some((i: any) => i?.type === 'question_ui'));
      const hasSimulationApprovalInterrupt = !!(targetBotMsg && Array.isArray((targetBotMsg as any).interrupts) && (targetBotMsg as any).interrupts.some((i: any) => i?.type === 'simulation_approval_ui'));

      if (hasQuestionInterrupt) {
        const qi = (targetBotMsg as any).interrupts.find((i: any) => i?.type === 'question_ui');
        const interruptId = qi?.data?.interrupt_id;
        const chatId = currentChat?.chatId;
        await respondWithEditedMessage('answer', newText.trim(), { interruptId, chatId, botMessageId: botMessageId, userMessageId: editingMessageId });
      } else if (hasSimulationApprovalInterrupt) {
        const sai = (targetBotMsg as any).interrupts.find((i: any) => i?.type === 'simulation_approval_ui');
        const interruptId = sai?.data?.interrupt_id;
        const chatId = currentChat?.chatId;
        await respondWithEditedMessage('modify', newText.trim(), { interruptId, chatId, botMessageId: botMessageId, userMessageId: editingMessageId });
      } else {
        await sseEditMessage(lastActiveChatId, editingMessageId, newText.trim());
      }
    }
  }, [editingMessageId, sseEditMessage, currentChat?.messages, lastActiveChatId, respondWithEditedMessage]);

  const handleCopyMessage = async () => {
    try {
      // Convert context tags back to @mentions for copying
      let textToCopy = message.text || '';
      const contextTagRegex = /<context>(.*?)<\/context>/g;
      textToCopy = textToCopy.replace(contextTagRegex, (match, jsonStr) => {
        try {
          const obj = JSON.parse(jsonStr);
          if (obj && obj.type === 'team') {
            const baseName = obj.team_name || obj.name;
            return baseName ? `@${baseName}` : match;
          }
          const displayName = obj?.name;
          return displayName ? `@${displayName}` : match;
        } catch (e) {
          const legacy = match.match(/<context>\{"type":\s*"([^"]+)",\s*"id":\s*"([^"]+)",\s*"name":\s*"([^"]+)"\}<\/context>/);
          return legacy ? `@${legacy[3]}` : match;
        }
      });

      await navigator.clipboard.writeText(textToCopy);

      // Show a brief success indication with tick mark
      const copyButton = document.querySelector(`[data-message-id="${message.id || 'temp'}"] .copy-button`);
      if (copyButton) {
        const originalContent = copyButton.innerHTML;
        copyButton.innerHTML = '<svg class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>';
        setTimeout(() => {
          copyButton.innerHTML = originalContent;
        }, 1000);
      }
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setInputText('');
  }, []);

  const handleEditMessage = useCallback((messageId: number, currentText: string) => {
    if (!canEdit) {
      return;
    }
    setEditingMessageId(messageId);
    // Convert context tags to @mentions for editing
    const textWithMentions = convertContextTagsToMentions(currentText);
    setInputText(textWithMentions);
  }, [convertContextTagsToMentions, canEdit]);

  const handleSaveEditWithMentions = () => {
    if (!inputText.trim()) return;

    if (typeof getTextWithContextTags === 'function') {
      handleSaveEdit(getTextWithContextTags());
    } else {
      handleSaveEdit(inputText);
    }
  };

  const handleCollapsedMessageClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!isMessageLong) return;

    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (selection && selection.toString().length > 0) {
      return;
    }

    setIsCollapsed(prev => !prev);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((showSuggestions && suggestions && suggestions.length > 0) || (showDefaultOptions && defaultCategories.length > 0)) {
      handleMentionKeyDown(e);
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEditWithMentions();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const memoizedUserMessageContent = useCallback((message: Message) => {

    // For non-bot messages, convert context tags back to @mentions for display
    if (!message.isBot) {
      const text = message.text || '';
      const contextTagRegex = /<context>(.*?)<\/context>/g;

      const parts: any[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      let hasMentions = false;

      while ((match = contextTagRegex.exec(text)) !== null) {
        hasMentions = true;
        const fullMatch = match[0];
        const jsonStr = match[1];

        // Add text before the mention
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }

        // Parse JSON to get display label and id
        let entityId: string = '';
        let label: string = '';
        let entityType: string = '';
        try {
          const obj = JSON.parse(jsonStr);
          entityType = obj?.type??"na";
          entityId = String(obj.id ?? '');
          if (obj && obj.type === 'team') {
            const baseName = obj.team_name || obj.name;
            label = baseName ? `${baseName}${obj.project_name ? ` (${obj.project_name})` : ''}` : '';
          } else {
            label = obj?.name || '';
          }
        } catch {
          // Fallback to legacy fixed-shape pattern
          const legacy = fullMatch.match(/<context>\{"type":\s*"([^"]+)",\s*"id":\s*"([^"]+)",\s*"name":\s*"([^"]+)"\}<\/context>/);
          if (legacy) {
            entityId = legacy[2];
            label = legacy[3];
          }
        }



        if (label) {
          let Icon = null;
          let bgClass = "bg-blue-200 dark:bg-blue-800";
          let textClass = "text-black dark:text-blue-100";
          
          if (entityType && entityType !== 'na') {
             // If entityType is explicit (epic/feature), getEntityTypeDisplay handles it.
             // However, getEntityTypeDisplay signature is (entityType?, type?).
             // We can try passing entityType as first arg.
             if(entityType == "strategymap") Icon = HiOutlineViewBoards;
             if(entityType == "epic") Icon = HiOutlineBriefcase;
             if(entityType == "feature") Icon = HiOutlineBriefcase;
          }

          parts.push(
            <span key={`${entityId || label}-${match.index}`} className="bg-blue-200 text-black dark:bg-blue-800 dark:text-blue-100 rounded-md">
              {`@${label}`}
            </span>
          );
        } else {
          // If parsing failed, keep raw tag
          parts.push(fullMatch);
        }
        lastIndex = match.index + fullMatch.length;
      }

      // Add any remaining text after the last mention
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      // If there were no context tags, just display the raw text.
      if (!hasMentions) {
        const displayText = convertContextTagsToMentions(message.text);
        return <p className="whitespace-pre-wrap">{displayText}</p>;
      }

      return <p className="whitespace-pre-wrap">{parts.map((part, i) => <Fragment key={i}>{part}</Fragment>)}</p>;
    }

    return null;
  }, [currentChat]);

  useEffect(() => {
    const shouldShow = showSuggestions || showDefaultOptions;
    setShowAtDropdown(shouldShow);
  }, [showSuggestions, showDefaultOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showAtDropdown) return;
      const target = event.target as Node;
      if (dropdownRef.current && dropdownRef.current.contains(target)) return;
      if (inputTextRef.current && inputTextRef.current.contains(target)) return;
      if (atButtonRef.current && atButtonRef.current.contains(target)) return;
      closeMentionDropdown();
      setShowAtDropdown(false);
    };
    if (showAtDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAtDropdown, closeMentionDropdown]);

  useEffect(() => {
    if (isCurrentlyEditing && message.text && convertContextTagsToMentions) {
      const convertedText = convertContextTagsToMentions(message.text);
      setInputText(convertedText);
      // --- Parse context tags and set mentions array (flexible JSON) ---
      const contextTagRegex = /<context>(.*?)<\/context>/g;
      const mentionsArr = [];
      let match;
      let searchStart = 0;
      while ((match = contextTagRegex.exec(message.text)) !== null) {
        const fullMatch = match[0];
        const jsonStr = match[1];
        let type, entity_id = "", name, team_name, project_name;
        try {
          const obj = JSON.parse(jsonStr);
          type = obj.type;
          entity_id = String(obj.id);
          name = obj.name;
          if (type === 'team') {
            team_name = obj.team_name || name;
            project_name = obj.project_name || '';
          }
        } catch (e) {
          const legacy = fullMatch.match(/<context>\{"type":\s*"([^"]+)",\s*"id":\s*"([^"]+)",\s*"name":\s*"([^"]+)"\}<\/context>/);
          if (legacy) {
            type = legacy[1];
            entity_id = legacy[2];
            name = legacy[3];
          }
        }
        const displayName = type === 'team' ? (team_name || name) : name;
        if (!displayName) continue;
        // For textarea display we show 'Team (Project)', but here we need to find positions in convertedText which also uses that label in edit mode
        const label = type === 'team' ? `${displayName}${project_name ? ` (${project_name})` : ''}` : displayName;
        const mentionText = `@${label}`;
        let start = convertedText.indexOf(mentionText, searchStart);
        if (start === -1) start = convertedText.indexOf(mentionText);
        const end = start !== -1 ? start + mentionText.length : -1;
        if (start !== -1) searchStart = end;
        const mentionObj: Mention = {
          id: entity_id,
          name: label,
          type,
          entity_id,
          start: start !== -1 ? start : 0,
          end: end !== -1 ? end : 0,
          completed: true
        };
        if (type === 'team') {
          mentionObj.team_name = displayName;
          mentionObj.project_name = project_name || '';
        }
        mentionsArr.push(mentionObj);
      }
      setMentions(mentionsArr);
    }
  }, [isCurrentlyEditing, message.text, convertContextTagsToMentions, setMentions]);

  if(!isCurrentlyEditing) {
    const shouldShowCollapsed = isMessageLong && isCollapsed;

    return (
      <div className="flex min-w-0 w-full py-2 sm:py-3 text-gray-400 text-xs sm:text-sm justify-end user-message group" data-message-id={message.id || 'temp'}>
        <div className="relative max-w-[85%] sm:max-w-[75%]">
          <button
            className={`text-start px-2 sm:px-3 py-1.5 sm:py-2 text-gray-800 dark:text-gray-200 text-[13px] sm:text-[15px] rounded-2xl sm:rounded-3xl font-normal font-['Inter'] 
              leading-relaxed bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm opacity-90 relative 
              ${shouldShowCollapsed ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200' : ''} 
              ${shouldShowCollapsed ? 'max-h-36 overflow-hidden' : ''}
            `}
            style={{ userSelect: 'text', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            onClick={handleCollapsedMessageClick}
            title={shouldShowCollapsed ? 'Click to expand full message' : undefined}
          >
            <div className="whitespace-pre-wrap break-words">
              {memoizedUserMessageContent(message)}
            </div>
            {shouldShowCollapsed && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 dark:from-gray-800 to-transparent pointer-events-none rounded-b-2xl sm:rounded-b-3xl"></div>
            )}
          </button>

          {/* Copy button - appears on hover */}
          <button
            onClick={handleCopyMessage}
            className="copy-button absolute -bottom-3.5 right-3 w-6 h-6 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600"
            title="Copy message"
          >
            <HiOutlineDocumentDuplicate className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300" />
          </button>

          {canEdit && (
            <button
              onClick={() => message.messageId && handleEditMessage(message.messageId, message.text)}
              className="edit-button absolute -bottom-3.5 right-12 w-6 h-6 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit message"
            >
              <HiPencil className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 w-full py-2 sm:py-3 text-gray-400 text-xs sm:text-sm justify-end user-message group">
      <div className='w-full relative'>
        <MentionTextarea
          value={inputText}
          onChange={handleMentionInputChange}
          onKeyDown={handleEditKeyDown}
          inputRef={inputTextRef}
          mentions={mentions}
          wrapperClassName="pb-6 sm:pb-8"
          className="block w-full mb-3 sm:mb-4 px-1 py-1.5 sm:py-2 text-[13px] sm:text-[15px] text-gray-900 dark:text-gray-100 bg-transparent focus:ring-0 focus:outline-none resize-none placeholder-gray-500 dark:placeholder-gray-400 border-b border-gray-200 dark:border-gray-700"
          placeholder="Edit your message..."
        />

        <div className="flex justify-end items-center gap-3 mt-1.5 border-t border-gray-100 dark:border-gray-700" style={{ padding: '6px 8px 2px 0px' }}>
          <button
            onClick={handleCancelEdit}
            className="px-1 py-0.5 text-xs text-gray-500 dark:text-gray-300 hover:text-gray-300 dark:hover:text-gray-500 transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEditWithMentions}
            className="px-2 py-1 bg-primary-light text-white rounded dark:bg-primary-dark text-xs dark:text-white hover:text-blue-300 dark:hover:text-blue-300 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!inputText.trim()}
          >
            Send
          </button>
        </div>

        <AtDropdown
          key={`${currentChat?.chatId || 'chat'}-${isCurrentlyEditing ? 'edit' : 'view'}-${message.messageId || 'temp'}`}
          showDropdown={showAtDropdown}
          handleRoadmapSelectInternal={handleRoadmapSelectInternal}
          dropdownRef={dropdownRef}
          textareaRef={inputTextRef}
          onBackendSearch={handleSearch}
          preferBelow
          selectedSuggestionIndex={selectedSuggestionIndex}
          suggestions={suggestions}
          loadingSearch={loadingSearch}
          loadingMore={loadingMore}
          isDropdownFromButton={isDropdownFromButton}
          hasMoreAll={hasMoreAll}
          hasMoreWorkUnits={hasMoreWorkUnits}
          hasMoreStrategyMaps={hasMoreStrategyMaps}
          hasMoreTeams={hasMoreTeams}
          hasMoreResources={hasMoreResources}
          onLoadMore={loadMore}
          initialSearchType={currentSearchType}
          currentSearchTerm={currentSearchTerm}
        />
      </div>
    </div>
  );
};

export default MemoizedUserMessage;